// Accept an operator invite + set a password. Public (the invitee has no session).
// The invite email links here: admin.dev.kazipeople.com/invite/accept?token=…
import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Form, Input, Button, Typography, Alert, Result } from 'antd';
import { LockOutlined } from '@ant-design/icons';
import { validateInvite, acceptInvite } from '../../services/operatorsService';

const AcceptInvitePage: React.FC = () => {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const navigate = useNavigate();
  const [info, setInfo] = useState<{ email?: string; role?: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) { setError('This invite link is missing its token.'); return; }
    // Best-effort: show who the invite is for. If validation isn't reachable we
    // still let them set a password (accept will reject a bad/expired token).
    validateInvite(token)
      .then((d: any) => setInfo({ email: d?.email, role: d?.role }))
      .catch(() => {});
  }, [token]);

  const onFinish = async (v: { password: string }) => {
    setError(null); setSubmitting(true);
    try {
      await acceptInvite(token, v.password);
      setDone(true);
      setTimeout(() => navigate('/login'), 2500);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'This invite is invalid or has expired. Ask an owner to re-send it.');
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <Result
        status="success"
        title="You're all set"
        subTitle="Your password is set. Redirecting you to sign in…"
        extra={<Link to="/login"><Button type="primary">Go to sign in</Button></Link>}
      />
    );
  }

  return (
    <div style={{ maxWidth: 380, margin: '0 auto', padding: 24 }}>
      <Typography.Title level={3} style={{ marginBottom: 4 }}>Join KaziPeople Platform</Typography.Title>
      <Typography.Paragraph type="secondary">
        {info?.email
          ? <>Set a password for <strong>{info.email}</strong>{info.role ? ` (${info.role.replace('platform_', '')})` : ''}.</>
          : 'Set your password to activate your operator account.'}
      </Typography.Paragraph>

      {error && <Alert type="error" showIcon style={{ marginBottom: 16 }} message={error} />}

      {!token ? null : (
        <Form layout="vertical" requiredMark={false} onFinish={onFinish}>
          <Form.Item name="password" label="Password" rules={[
            { required: true, message: 'Choose a password' },
            { min: 8, message: 'At least 8 characters' },
          ]}>
            <Input.Password prefix={<LockOutlined />} placeholder="••••••••" autoComplete="new-password" />
          </Form.Item>
          <Form.Item name="confirm" label="Confirm password" dependencies={['password']} rules={[
            { required: true, message: 'Confirm your password' },
            ({ getFieldValue }) => ({
              validator: (_, value) =>
                !value || getFieldValue('password') === value ? Promise.resolve() : Promise.reject(new Error('Passwords do not match')),
            }),
          ]}>
            <Input.Password prefix={<LockOutlined />} placeholder="••••••••" autoComplete="new-password" />
          </Form.Item>
          <Button type="primary" htmlType="submit" block loading={submitting}>Set password & continue</Button>
        </Form>
      )}

      <Typography.Paragraph type="secondary" style={{ marginTop: 16, textAlign: 'center' }}>
        Already set up? <Link to="/login">Sign in</Link>
      </Typography.Paragraph>
    </div>
  );
};

export default AcceptInvitePage;
